import { useState, useEffect } from "react";
import { useApp } from "../context/AppContext";
import PromptLibraryModal from "./PromptLibraryModal";
const API_BASE = import.meta.env.VITE_API_URL || "";

export default function ModelModal({ model, allModels, onClose, onSuccess }) {
  const { showToast, fetchModels } = useApp();
  const [loading, setLoading] = useState(false);
  const [globalDefaults, setGlobalDefaults] = useState(null);
  const [pendingReset, setPendingReset] = useState(false);
  const [showPromptLibrary, setShowPromptLibrary] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Initialize state safely (handles model being null on first render)
  const isCustom = model?.isCustom || false;
  const isOkeyHelp = model?.provider === "okeybot" && (model?.name === "okeyhelp" || model?.name === "okeybot/okeyhelp");

  const [baseProvider, setBaseProvider] = useState(isCustom ? (model.provider || "") : "");
  const [baseModelId, setBaseModelId] = useState(isCustom ? (model.baseModelId || "") : "");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const [modelSettings, setModelSettings] = useState({
    displayName: model?.displayName || "",
    systemPrompt: model?.systemPrompt || "",
    supportsStreaming: !!model?.supportsStreaming,
    supportsVision: !!model?.supportsVision,
    supportsImage: !!model?.supportsImage,
    supportsVideo: !!model?.supportsVideo,
    supportsChat: !!model?.supportsChat,
  });

  // Sync when model changes
  useEffect(() => {
    if (model) {
      setModelSettings({
        displayName: model.displayName || "",
        systemPrompt: model.systemPrompt || "",
        supportsStreaming: !!model.supportsStreaming,
        supportsVision: !!model.supportsVision,
        supportsImage: !!model.supportsImage,
        supportsVideo: !!model.supportsVideo,
        supportsChat: !!model.supportsChat,
      });
      if (model.isCustom) {
        setBaseProvider(model.provider || "");
        setBaseModelId(model.baseModelId || "");
      }
    }
  }, [model]);

  if (!model) return null;

  // Prepare data for the Combobox
  const flatModels = allModels?.flatMap(pg =>
    (pg.models || []).filter(m => !m.isCustom).map(m => ({ ...m, provider: pg.provider }))
  ) || [];

  const filteredModels = flatModels.filter(m => {
    const term = searchTerm.trim().toLowerCase();
    const name = m.name || "";
    const display = m.displayName || "";
    const provider = m.provider || "";
    return (
      display.toLowerCase().includes(term) ||
      name.toLowerCase().includes(term) ||
      provider.toLowerCase().includes(term)
    );
  });
  // Reset highlight when search changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchTerm]);

  const handleKeyDown = (e) => {
    if (!searchOpen) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex(prev => (prev < filteredModels.length - 1 ? prev + 1 : prev));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex(prev => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "Enter" && highlightedIndex >= 0) {
      e.preventDefault();
      const m = filteredModels[highlightedIndex];
      if (m) {
        setBaseProvider(m.provider);
        setBaseModelId(m.name);
        setSearchOpen(false);
        setSearchTerm("");
      }
    } else if (e.key === "Escape") {
      setSearchOpen(false);
    }
  };

  const getDisplayValue = () => {
    if (searchOpen) return searchTerm;
    if (baseModelId) {
      const found = flatModels.find(m => m.name === baseModelId && m.provider === baseProvider);
      if (found) return `${found.displayName} (${found.provider})`;
      return baseModelId;
    }
    return "";
  };

  function isEqualToDefaults() {
    if (!globalDefaults) return false;
    return (
      modelSettings.displayName === globalDefaults.displayName &&
      (modelSettings.systemPrompt || "").trim() === "" &&
      !!modelSettings.supportsStreaming === !!globalDefaults.supportsStreaming &&
      !!modelSettings.supportsVision === !!globalDefaults.supportsVision &&
      !!modelSettings.supportsImage === !!globalDefaults.supportsImage &&
      !!modelSettings.supportsVideo === !!globalDefaults.supportsVideo &&
      !!modelSettings.supportsChat === !!globalDefaults.supportsChat
    );
  }

  async function saveModelSettings() {
    setLoading(true);

    if (model.isCustom) {
      // Update Custom Model
      try {
        const res = await fetch(`${API_BASE}/api/models/custom/${model.id}`, {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            display_name: modelSettings.displayName,
            provider: baseProvider,
            base_model_id: baseModelId,
            system_prompt: modelSettings.systemPrompt,
            capabilities: {
              supportsStreaming: modelSettings.supportsStreaming,
              supportsVision: modelSettings.supportsVision,
              supportsImage: modelSettings.supportsImage,
              supportsVideo: modelSettings.supportsVideo,
              supportsChat: modelSettings.supportsChat,
            }
          }),
        });
        if (!res.ok) throw new Error("Failed to update custom model");
        showToast("Custom model updated successfully", "success");
        fetchModels();
        if (onSuccess) onSuccess();
        onClose();
      } catch (err) {
        showToast(err.message, "error");
      } finally {
        setLoading(false);
      }
      return;
    }

    // Update Standard Model Settings
    const doResetPersist = pendingReset && isEqualToDefaults();
    const req = doResetPersist
      ? fetch(`${API_BASE}/api/models/${model.id}/reset-to-default`, {
        method: "POST",
        credentials: "include",
      })
      : fetch(`${API_BASE}/api/models/${model.id}/settings`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(modelSettings),
      });
    req
      .then((r) => {
        if (!r.ok) throw new Error(doResetPersist ? "Failed to reset to defaults" : "Failed to save settings");
        showToast(doResetPersist ? "Reset to global defaults" : "Settings saved successfully", "success");
        fetchModels();
        if (onSuccess) onSuccess();
        onClose();
      })
      .catch((err) => showToast(err.message, "error"))
      .finally(() => setLoading(false));
  }

  function resetToDefault() {
    if (loading) return;
    setLoading(true);
    fetch(`${API_BASE}/api/models/${model.id}/defaults`, { credentials: "include" })
      .then((r) => {
        if (!r.ok) throw new Error("Failed to load defaults");
        return r.json();
      })
      .then((defs) => {
        setGlobalDefaults(defs);
        setModelSettings({
          displayName: defs.displayName,
          systemPrompt: "",
          supportsStreaming: !!defs.supportsStreaming,
          supportsVision: !!defs.supportsVision,
          supportsImage: !!defs.supportsImage,
          supportsVideo: !!defs.supportsVideo,
          supportsChat: !!defs.supportsChat,
        });
        setPendingReset(true);
        showToast("Defaults loaded. Press Save to apply.", "info");
      })
      .catch((err) => showToast(err.message || "Failed to load defaults", "error"))
      .finally(() => setLoading(false));
  }

  function handleInsertPrompt(text) {
    setModelSettings(prev => ({
      ...prev,
      systemPrompt: (prev.systemPrompt || "").trim()
        ? (prev.systemPrompt.trim() + "\n\n" + text)
        : text
    }));
    setShowPromptLibrary(false);
  }

  async function handleDelete() {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/models/custom/${model.id}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (!res.ok) throw new Error("Failed to delete");
      showToast("Custom model deleted", "success");
      if (onSuccess) onSuccess(); // Refresh the list
      onClose();
    } catch (e) {
      showToast(e.message, "error");
      setLoading(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 2000 }}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Model Settings</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Display Name</label>
            <input
              className="input"
              type="text"
              value={modelSettings.displayName}
              onChange={e =>
                setModelSettings({ ...modelSettings, displayName: e.target.value })
              }
              placeholder="Custom display name"
            />

            {!model.isCustom && (
              <div style={{ fontSize: "0.875rem", color: "#9ca3af", marginTop: "0.25rem" }}>
                Model ID: <span style={{ fontFamily: "monospace" }}>{model.name}</span>
              </div>
            )}
          </div>
          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <label className="form-label" style={{ marginBottom: 0 }}>System Prompt (Optional)</label>
              {!isOkeyHelp && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ padding: "0.25rem 0.75rem", fontSize: "0.75rem", borderRadius: "0.5rem", }}
                  onClick={() => setShowPromptLibrary(true)}
                >
                  Prompt Library
                </button>
              )}
            </div>
            <textarea
              className="input"
              value={isOkeyHelp ? "This model uses a strictly locked system prompt tuned specifically for OkeyBot support." : modelSettings.systemPrompt}
              onChange={e =>
                setModelSettings({ ...modelSettings, systemPrompt: e.target.value })
              }
              placeholder="Add a custom system prompt for this model..."
              rows={4}
              disabled={isOkeyHelp}
            />
            <div style={{ fontSize: "0.875rem", color: "#9ca3af", marginTop: "0.25rem" }}>
              {isOkeyHelp ? (
                <strong>System prompt is locked to ensure the support persona functions correctly.</strong>
              ) : (
                <><strong>Model-specific prompt:</strong> This overrides the global prompt for this model only.</>
              )}
            </div>
          </div>


          {model.isCustom && (
            <div className="form-group">
              <label className="form-label">Base Model</label>
              <div className="model-selector-wrapper">
                <input
                  className="model-selector-input"
                  placeholder="Search for a model..."
                  value={getDisplayValue()}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setSearchOpen(true);
                  }}
                  onKeyDown={handleKeyDown}
                  onClick={() => {
                    setSearchOpen(true);
                    setSearchTerm("");
                  }}
                  onBlur={() => setTimeout(() => setSearchOpen(false), 200)}
                />
                <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-muted)' }}>▼</div>

                {searchOpen && (
                  <div className="model-selector-dropdown">
                    {filteredModels.length === 0 ? (
                      <div style={{ padding: "0.75rem", color: "var(--text-muted)", fontSize: "0.875rem" }}>
                        No models found
                      </div>
                    ) : (
                      filteredModels.map((m, idx) => (
                        <div
                          key={`${m.provider}-${m.name}`}
                          className={`model-selector-option ${idx === highlightedIndex ? "focused" : ""}`}
                          onMouseDown={() => {
                            setBaseProvider(m.provider);
                            setBaseModelId(m.name);
                            setSearchOpen(false);
                          }}
                        >
                          <span className="model-selector-badge">{m.provider}</span>
                          {m.displayName}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
              <div style={{ fontSize: "0.875rem", color: "#9ca3af", marginTop: "0.25rem" }}>
                The intelligence engine used by this persona.
              </div>
            </div>
          )}
          <div className="form-group">
            <label className="form-label" style={{ marginBottom: "0.5rem" }}>Capabilities</label>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: "0.75rem" }}>
              <label className="cap-checkbox-label">
                <input
                  type="checkbox"
                  checked={modelSettings.supportsStreaming}
                  disabled={isOkeyHelp}
                  onChange={e => setModelSettings({ ...modelSettings, supportsStreaming: e.target.checked })}
                />
                Streaming
              </label>
              <label className="cap-checkbox-label">
                <input
                  type="checkbox"
                  checked={modelSettings.supportsChat}
                  disabled={isOkeyHelp}
                  onChange={e => setModelSettings({ ...modelSettings, supportsChat: e.target.checked })}
                />
                Chat
              </label>
              <label className="cap-checkbox-label">
                <input
                  type="checkbox"
                  checked={modelSettings.supportsVision}
                  disabled={isOkeyHelp}
                  onChange={e => setModelSettings({ ...modelSettings, supportsVision: e.target.checked })}
                />
                Vision
              </label>
              <label className="cap-checkbox-label">
                <input
                  type="checkbox"
                  checked={modelSettings.supportsImage}
                  disabled={isOkeyHelp}
                  onChange={e => setModelSettings({ ...modelSettings, supportsImage: e.target.checked })}
                />
                Image
              </label>
              <label className="cap-checkbox-label">
                <input
                  type="checkbox"
                  checked={modelSettings.supportsVideo}
                  disabled={isOkeyHelp}
                  onChange={e => setModelSettings({ ...modelSettings, supportsVideo: e.target.checked })}
                />
                Video
              </label>
            </div>
          </div>

        </div>
        <div className="modal-footer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            {!model.isCustom && (
              <button
                type="button"
                onClick={resetToDefault}
                disabled={loading}
                className="btn-reset-link"
                style={{
                  background: 'none',
                  color: '#888',
                  border: 'none',
                  padding: 0,
                  fontSize: '0.93em',
                  textDecoration: 'underline',
                  cursor: 'pointer'
                }}
              >
                Reset to Default
              </button>
            )}
            {model.isCustom && (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={loading}
                className="btn btn-danger"
                style={{
                  padding: "0.5rem 1rem",
                  fontSize: "0.875rem",
                  background: "var(--error)",
                  color: "white",
                  border: "none"
                }}
              >
                Delete Model
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button className="btn btn-secondary" onClick={onClose} type="button">
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={saveModelSettings}
              disabled={loading}
              type="button"
            >
              {loading ? "Saving..." : "Save Settings"}
            </button>
          </div>
        </div>
      </div>
      {showPromptLibrary && (
        <div style={{ position: 'relative', zIndex: 3000 }}>
          <PromptLibraryModal
            onClose={() => setShowPromptLibrary(false)}
            onInsert={handleInsertPrompt}
          />
        </div>
      )}

      {showDeleteConfirm && (
        <div className="modal-overlay" style={{ zIndex: 3200 }} onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-content" style={{ maxWidth: "400px" }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Model?</h2>
              <button className="modal-close" onClick={() => setShowDeleteConfirm(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ color: "var(--text-secondary)" }}>
                Are you sure you want to delete <strong style={{ color: "var(--text-primary)" }}>{modelSettings.displayName}</strong>? This action cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowDeleteConfirm(false)} disabled={loading}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete} disabled={loading}>
                {loading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
